/**
 * GomarkePixel - Pixel Tracker para Facebook e Google Analytics
 * Versão: 1.0.0
 */

import { AutoTrackerFactory } from './conversion/autoTracks/AutoTrackerFactory';
import {
  initConversionEngine,
  trackConversion,
  getConversionTrack,
  getConversionTrackerFactory,
  updateConversionConfig,
} from './conversion/engine';
import { initAutoTrackEngine } from './conversion/autoTrackEngine';
import type { AutoTrackConfig } from './conversion/autoTrackEngine';
import type { TrackerFactory } from './conversion/tracks/TrackerFactory';
import { ILeadData } from './conversion/tracks/lead';
import { IPurchaseData } from './conversion/tracks/purchase';
import { IAddToCartData } from './conversion/tracks/addToCart';
import { IInitiateCheckoutData } from './conversion/tracks/initiateCheckout';
import { IAddPaymentInfoData } from './conversion/tracks/addPaymentInfo';
import { IRemoveToCartData } from './conversion/tracks/removeToCart';
import { ISearchData } from './conversion/tracks/search';
import { IViewContentData } from './conversion/tracks/viewContent';
import { IAutoAddToCartConfig } from './conversion/autoTracks/autoAddToCart';
import { IAutoInitiateCheckoutConfig } from './conversion/autoTracks/autoInitiateCheckout';
import { IAutoAddPaymentInfoConfig } from './conversion/autoTracks/autoAddPaymentInfo';
import { IAutoCustomerTrackerConfig } from './conversion/autoTracks/autoCustomerTracker';
import { IAutoCustomerAddToCartConfig } from './conversion/autoTracks/autoCustomerAddToCart';
import { IAutoLeadConfig } from './conversion/autoTracks/autoLead';
import { IAutoRemoveToCartConfig } from './conversion/autoTracks/autoRemoveToCart';
import { IAutoViewContentConfig } from './conversion/autoTracks/autoViewContent';
import { GomarkePixelConfig, PixelResponse } from './types';
import { loadFacebookPixels } from './utils/loadFacebookPixel';
import { GetSession } from './utils/getSession';
import { SelectorType } from './conversion/autoTracks';
import {
  initCoreTracker,
  getCoreSessionId,
  getCoreVisitorId,
  getSession as getCoreSession,
  getQueueLength,
  flushQueue,
  getQueueStats,
  getQueueTelemetry,
  getStructuredTelemetry,
  getDeadLetterCount,
  exportDeadLetterData,
  configureQueueIndex,
  getRuntimeDebugSnapshot,
} from './core';
import {
  initBehaviorEngine,
  getBehaviorState,
  getViewportState,
  resetForNewPage,
  getBehaviorBufferStatsExport,
  getBehaviorStateMachine,
  getBehaviorBatchPayload,
  enqueueBehaviorSnapshot,
} from './behavior';

class GomarkePixel {
  private config: GomarkePixelConfig;
  private version: string = '1.0.0';
  private sessionId: string;
  private sessionManager: GetSession;
  private autoTrackerFactory: AutoTrackerFactory;

  constructor(config: GomarkePixelConfig) {
    if (!config.apiEndpoint) {
      throw new Error('apiEndpoint é obrigatório');
    }

    this.config = {
      debug: false,
      timeout: 5000,
      version: this.version,
      ...config,
    };

    if (this.config.queueIndex) {
      configureQueueIndex(true);
    }

    // Inicializa sessão persistente
    this.sessionManager = new GetSession(undefined, this.config.debug);
    this.sessionId = this.sessionManager.getSessionId();

    this.autoTrackerFactory = new AutoTrackerFactory(
      this.config,
      this.sessionId,
      this.version
    );

    // Inicializa o pixel
    this.initialize();
  }

  private async initialize(): Promise<void> {
    try {
      // Fase 1 Core Engine: Visitor → Session → PageView (SPA + unload)
      await initCoreTracker({
        config: this.config,
        version: this.version,
        withFingerprint: false,
        newSessionOnDomainChange: false,
      });
      const coreSessionId = getCoreSessionId();
      const coreVisitorId = getCoreVisitorId();
      if (coreSessionId) {
        this.sessionId = coreSessionId;
        this.autoTrackerFactory.updateSessionId(coreSessionId);
      }
      if (coreVisitorId) {
        this.autoTrackerFactory.updateVisitorId(coreVisitorId);
      }

      // Fase 2 Behavior Engine: scroll, click, idle, exit intent, viewport, engagement
      initBehaviorEngine({
        config: this.config,
        version: this.version,
        behavior: this.config.behavior,
      });

      if (this.config.debug) {
        setTimeout(() => {
          console.log(
            '[GomarkePixel] Viewport state após initBehaviorEngine:',
            getViewportState()
          );
        }, 250);
      }

      // Meta: Facebook Pixel (um ou vários externalId)
      if (this.config.pixelMeta && Array.isArray(this.config.pixelMeta)) {
        const metaPixelIds = this.config.pixelMeta
          .map((pixel: { externalId?: string }) => pixel?.externalId)
          .filter((id): id is string => Boolean(id));
        loadFacebookPixels(metaPixelIds);
      }

      // Fase 3 Conversion Engine: track centralizado (após Core e Behavior)
      initConversionEngine({
        config: this.config,
        version: this.version,
        sessionId: coreSessionId ?? undefined,
        visitorId: coreVisitorId ?? undefined,
      });

      this.autoTrackerFactory.setTrackConversion(getConversionTrack());

      if (this.config.autoTrack) {
        initAutoTrackEngine(
          this.config.autoTrack as AutoTrackConfig,
          getConversionTrack
        );
      }

      this.init();
      this.initializeAutoTracks();
    } catch (error) {
      if (this.config.debug) {
        console.error('[GomarkePixel] Erro na inicialização:', error);
      }
      // Mesmo com erro, inicializa o básico
      this.init();
    }
  }

  private init(): void {
    if (this.config.debug) {
      console.log('GomarkePixel inicializado:', this.config);
    }

    // PageView é enviado pelo Core (SPA + unload com scroll/duração)
    // Chamada manual ainda disponível via trackPageView()
  }

  /**
   * Inicializa autoTracks automaticamente baseado na configuração
   */
  private async initializeAutoTracks(): Promise<void> {
    if (!this.config.autoTracks) {
      return;
    }

    const { autoTracks } = this.config;

    try {
      // Auto-tracking de Add to Cart
      if (autoTracks.addToCart?.enabled) {
        const addToCartConfig = {
          isEnabled: true,
          selector: autoTracks.addToCart.selector ?? 'data-add-to-cart',
          selectorType:
            autoTracks.addToCart.selectorType ?? ('attribute' as SelectorType),
          productDataSelectors: Object.assign(
            {
              id: 'data-product-id',
              name: 'data-product-name',
              price: 'data-product-price',
              category: 'data-product-category',
            },
            autoTracks.addToCart.productDataSelectors
          ),
        };

        await this.autoTrackerFactory.createAddToCartTracker(addToCartConfig);

        if (this.config.debug) {
          console.log('AutoAddToCart inicializado automaticamente');
        }
      }

      // Auto-tracking de Initiate Checkout
      if (autoTracks.initiateCheckout?.enabled) {
        const checkoutConfig = {
          isEnabled: true,
          selector:
            autoTracks.initiateCheckout.selector ?? 'data-initiate-checkout',
          selectorType:
            autoTracks.initiateCheckout.selectorType ??
            ('attribute' as SelectorType),
          trackOnNavigation:
            autoTracks.initiateCheckout.trackOnNavigation || false,
          navigationSelectors: autoTracks.initiateCheckout.navigationSelectors,
          cartDataSelectors: Object.assign(
            {
              total: 'data-cart-total',
              currency: 'data-cart-currency',
              items: 'data-cart-items',
            },
            autoTracks.initiateCheckout.cartDataSelectors
          ),
          customCartExtractor: autoTracks.initiateCheckout.customCartExtractor,
        };

        await this.autoTrackerFactory.createInitiateCheckoutTracker(
          checkoutConfig
        );

        if (this.config.debug) {
          console.log('AutoInitiateCheckout inicializado automaticamente');
        }
      }

      // Auto-tracking de Add Payment Info
      if (autoTracks.addPaymentInfo?.enabled) {
        const paymentInfoConfig: IAutoAddPaymentInfoConfig = {
          isEnabled: true,
          paymentMethod: autoTracks.addPaymentInfo.paymentMethod,
          detectCardFields: autoTracks.addPaymentInfo.detectCardFields,
          selector: autoTracks.addPaymentInfo.selector ?? 'data-checkout',
          selectorType:
            autoTracks.addPaymentInfo.selectorType ??
            ('attribute' as SelectorType),
          cartDataSelectors: Object.assign(
            {
              total: 'data-cart-total',
              currency: 'data-cart-currency',
              items: 'data-cart-items',
            },
            autoTracks.addPaymentInfo.cartDataSelectors
          ),
          customCartExtractor: autoTracks.addPaymentInfo.customCartExtractor,
          selectors: autoTracks.addPaymentInfo.selectors,
          debounceMs: autoTracks.addPaymentInfo.debounceMs,
          throttleMs: autoTracks.addPaymentInfo.throttleMs,
        };

        await this.autoTrackerFactory.createAddPaymentInfoTracker(
          paymentInfoConfig
        );

        if (this.config.debug) {
          console.log('AutoAddPaymentInfo inicializado automaticamente');
        }
      }

      const needsCustomerCapture =
        autoTracks.customer?.enabled || autoTracks.addPaymentInfo?.enabled;

      if (needsCustomerCapture) {
        const customerConfig: IAutoCustomerTrackerConfig = {
          isEnabled: true,
          selectors: autoTracks.customer?.selectors,
          debounceMs: autoTracks.customer?.debounceMs,
        };

        await this.autoTrackerFactory.createCustomerTracker(customerConfig);

        if (this.config.debug) {
          console.log('AutoCustomerTracker inicializado automaticamente');
        }
      }

      if (
        autoTracks.customer?.enabled &&
        autoTracks.customer?.autoAddToCart !== false
      ) {
        const customerAddToCartConfig: IAutoCustomerAddToCartConfig = {
          isEnabled: true,
        };

        await this.autoTrackerFactory.createCustomerAddToCartTracker(
          customerAddToCartConfig
        );

        if (this.config.debug) {
          console.log('AutoCustomerAddToCart inicializado automaticamente');
        }
      }

      // Auto-tracking de Lead
      if (autoTracks.lead?.enabled) {
        const leadConfig = {
          isEnabled: true,
          selector: autoTracks.lead.selector ?? 'data-lead',
          selectorType:
            autoTracks.lead.selectorType ?? ('attribute' as SelectorType),
          trackOnFieldFocus: autoTracks.lead.trackOnFieldFocus || false,
          trackOnFieldBlur: autoTracks.lead.trackOnFieldBlur || false,
          trackOnFieldChange: autoTracks.lead.trackOnFieldChange || false,
          requiredFields: autoTracks.lead.requiredFields,
          validationRules: autoTracks.lead.validationRules,
          formFieldSelectors: Object.assign(
            {
              email: 'input[name="email"]',
              name: 'input[name="name"]',
              phone: 'input[name="phone"]',
              message: 'textarea[name="message"]',
            },
            autoTracks.lead.formFieldSelectors
          ),
          customFormExtractor: autoTracks.lead.customFormExtractor,
        };

        await this.autoTrackerFactory.createLeadTracker(leadConfig);

        if (this.config.debug) {
          console.log('AutoLead inicializado automaticamente');
        }
      }

      // Auto-tracking de Remove to Cart
      if (autoTracks.removeToCart?.enabled) {
        const removeToCartConfig = {
          isEnabled: true,
          selector: autoTracks.removeToCart.selector ?? 'data-remove-to-cart',
          selectorType:
            autoTracks.removeToCart.selectorType ??
            ('attribute' as SelectorType),
          productDataSelectors: Object.assign(
            {
              id: 'data-product-id',
              name: 'data-product-name',
              price: 'data-product-price',
              category: 'data-product-category',
              currency: 'data-product-currency',
            },
            autoTracks.removeToCart.productDataSelectors
          ),
          customDataExtractor: autoTracks.removeToCart.customDataExtractor,
        };

        await this.autoTrackerFactory.createRemoveToCartTracker(
          removeToCartConfig
        );

        if (this.config.debug) {
          console.log('AutoRemoveToCart inicializado automaticamente');
        }
      }

      // Auto-tracking de View Content
      if (autoTracks.viewContent?.enabled) {
        const viewContentConfig = {
          isEnabled: true,
          selector: autoTracks.viewContent.selector ?? 'data-view-content',
          selectorType:
            autoTracks.viewContent.selectorType ??
            ('attribute' as SelectorType),
          customDataExtractor: autoTracks.viewContent.customDataExtractor,
        };

        await this.autoTrackerFactory.createViewContentTracker(
          viewContentConfig
        );

        if (this.config.debug) {
          console.log('AutoViewContent inicializado automaticamente');
        }
      }
    } catch (error) {
      if (this.config.debug) {
        console.error('Erro ao inicializar autoTracks:', error);
      }
    }
  }

  /**
   * Track Page View
   */
  public trackPageView(): Promise<PixelResponse> {
    return trackConversion('pageView');
  }

  /**
   * Track Purchase
   */
  public trackPurchase(purchaseData: IPurchaseData): Promise<PixelResponse> {
    return trackConversion('purchase', purchaseData);
  }

  /**
   * Track Add to Cart
   */
  public trackAddToCart(addToCartData: IAddToCartData): Promise<PixelResponse> {
    return trackConversion('addToCart', addToCartData);
  }

  /**
   * Track Initiate Checkout
   */
  public trackInitiateCheckout(
    checkoutData: IInitiateCheckoutData
  ): Promise<PixelResponse> {
    return trackConversion('initiateCheckout', checkoutData);
  }

  /**
   * Track Add Payment Info
   */
  public trackAddPaymentInfo(
    paymentData: IAddPaymentInfoData
  ): Promise<PixelResponse> {
    return trackConversion('addPaymentInfo', paymentData);
  }

  /**
   * Track Lead
   */
  public trackLead(leadData: ILeadData): Promise<PixelResponse> {
    return trackConversion('lead', leadData);
  }

  /**
   * Track Remove to Cart
   */
  public trackRemoveToCart(
    removeToCartData: IRemoveToCartData
  ): Promise<PixelResponse> {
    return trackConversion('removeToCart', removeToCartData);
  }

  /**
   * Track Search
   */
  public trackSearch(searchData: ISearchData): Promise<PixelResponse> {
    return trackConversion('search', searchData);
  }

  /**
   * Track View Content (VIEW_CONTENT)
   */
  public trackViewContent(
    viewContentData: IViewContentData = {}
  ): Promise<PixelResponse> {
    return trackConversion('viewContent', viewContentData);
  }

  /**
   * Atualiza a configuração
   */
  public updateConfig(newConfig: Partial<GomarkePixelConfig>): void {
    this.config = { ...this.config, ...newConfig };
    updateConversionConfig(newConfig);
  }

  /**
   * Obtém a configuração atual
   */
  public getConfig(): GomarkePixelConfig {
    return { ...this.config };
  }

  /**
   * Obtém a versão
   */
  public getVersion(): string {
    return this.version;
  }

  /**
   * Obtém o session ID (Core Engine quando disponível)
   */
  public getSessionId(): string {
    const coreId = getCoreSessionId();
    if (coreId) this.sessionId = coreId;
    else this.sessionId = this.sessionManager.getSessionId();
    return this.sessionId;
  }

  /**
   * Obtém o visitor ID do Core Engine (null se ainda não inicializado)
   */
  public getVisitorId(): string | null {
    return getCoreVisitorId();
  }

  /**
   * Obtém dados da sessão (Core quando disponível, senão fallback GetSession)
   */
  public getSessionData() {
    const coreSession = getCoreSession();
    if (coreSession) return coreSession;
    return this.sessionManager.getSessionData();
  }

  /**
   * Quantidade de eventos na fila do Core (para debug).
   */
  public getQueueLength(): number {
    return getQueueLength();
  }

  public getQueueStats(): ReturnType<typeof getQueueStats> {
    return getQueueStats();
  }

  public getQueueTelemetry(): ReturnType<typeof getQueueTelemetry> {
    return getQueueTelemetry();
  }

  public getStructuredTelemetry(): ReturnType<typeof getStructuredTelemetry> {
    return getStructuredTelemetry();
  }

  public configureQueueIndex(enabled: boolean): void {
    configureQueueIndex(enabled);
  }

  public getRuntimeDebugSnapshot(): ReturnType<typeof getRuntimeDebugSnapshot> {
    return getRuntimeDebugSnapshot();
  }

  public getDeadLetterCount(): number {
    return getDeadLetterCount();
  }

  public exportDeadLetterData(): string {
    return exportDeadLetterData();
  }

  /**
   * Envia imediatamente os eventos enfileirados (para debug/teste).
   */
  public async flushQueue(): Promise<void> {
    return flushQueue();
  }

  /**
   * Estado agregado do Behavior Engine (scroll, click, idle, visibility, exitIntent).
   */
  public getBehaviorState(): ReturnType<typeof getBehaviorState> {
    return getBehaviorState();
  }

  /**
   * Estado do viewport (comportamental): quantidade de elementos rastreados e visíveis.
   */
  public getViewportState(): ReturnType<typeof getViewportState> {
    return getViewportState();
  }

  /**
   * Reseta estado por página (scroll, click, exit intent, visibility). Útil em SPA.
   */
  public resetForNewPage(): void {
    resetForNewPage();
  }

  /** Etapa 2: estado da state machine (ACTIVE, IDLE, HIDDEN, EXITING). */
  public getBehaviorPhase(): string {
    return getBehaviorStateMachine().currentState;
  }

  /** Gera snapshot atual e enfileira como BEHAVIOR_SUMMARY (low). */
  public enqueueBehaviorSnapshot(): void {
    enqueueBehaviorSnapshot();
  }

  /** Etapa 2: estatísticas do buffer (snapshots e eventos críticos). */
  public getBehaviorBufferStats(): ReturnType<
    typeof getBehaviorBufferStatsExport
  > {
    return getBehaviorBufferStatsExport();
  }

  /** Etapa 2: payload de lote pronto para futura queue (não envia). */
  public getBehaviorBatchPayload(): ReturnType<
    typeof getBehaviorBatchPayload
  > | null {
    const vid = this.getVisitorId();
    const sid = this.getSessionId();
    if (!vid || !sid) return null;
    return getBehaviorBatchPayload(vid, sid);
  }

  /**
   * Verifica se a sessão está ativa
   */
  public isSessionActive(): boolean {
    return this.sessionManager.isActive();
  }

  /**
   * Atualiza a última atividade da sessão
   */
  public updateSessionActivity(): void {
    this.sessionManager.updateLastActivity();
  }

  /**
   * Renova a sessão
   */
  public renewSession(): void {
    this.sessionManager.renewSession();
    this.sessionId = this.sessionManager.getSessionId();
  }

  /**
   * Limpa a sessão
   */
  public clearSession(): void {
    this.sessionManager.clearSession();
    // Cria nova sessão após limpar
    this.sessionManager = new GetSession(undefined, this.config.debug);
    this.sessionId = this.sessionManager.getSessionId();
  }

  /**
   * Exporta dados da sessão
   */
  public exportSessionData(): string {
    return this.sessionManager.exportSessionData();
  }

  /**
   * Obtém o factory de trackers para acesso direto (ConversionEngine).
   * Retorna null se o ConversionEngine ainda não foi inicializado.
   */
  public getTrackerFactory(): TrackerFactory | null {
    return getConversionTrackerFactory();
  }

  /**
   * Obtém estatísticas dos trackers
   */
  public getTrackerStats(): { totalTrackers: number; trackerTypes: string[] } {
    return (
      getConversionTrackerFactory()?.getTrackerStats() ?? {
        totalTrackers: 0,
        trackerTypes: [],
      }
    );
  }

  /**
   * Limpa todos os trackers da memória
   */
  public clearTrackers(): void {
    getConversionTrackerFactory()?.clearTrackers();
  }

  // ===== MÉTODOS PARA AUTOTRACKS =====

  /**
   * Configura AutoAddToCart
   */
  public async setupAutoAddToCart(config: IAutoAddToCartConfig): Promise<void> {
    await this.autoTrackerFactory.createAddToCartTracker(config);
  }

  /**
   * Configura AutoInitiateCheckout
   */
  public async setupAutoInitiateCheckout(
    config: IAutoInitiateCheckoutConfig
  ): Promise<void> {
    await this.autoTrackerFactory.createInitiateCheckoutTracker(config);
  }

  /**
   * Configura AutoAddPaymentInfo
   */
  public async setupAutoAddPaymentInfo(
    config: IAutoAddPaymentInfoConfig
  ): Promise<void> {
    await this.autoTrackerFactory.createAddPaymentInfoTracker(config);
  }

  /**
   * Configura AutoLead
   */
  public async setupAutoLead(config: IAutoLeadConfig): Promise<void> {
    await this.autoTrackerFactory.createLeadTracker(config);
  }

  /**
   * Configura AutoRemoveToCart
   */
  public async setupAutoRemoveToCart(
    config: IAutoRemoveToCartConfig
  ): Promise<void> {
    await this.autoTrackerFactory.createRemoveToCartTracker(config);
  }

  /**
   * Configura AutoViewContent
   */
  public async setupAutoViewContent(
    config: IAutoViewContentConfig
  ): Promise<void> {
    await this.autoTrackerFactory.createViewContentTracker(config);
  }

  /**
   * Obtém o factory de autoTracks
   */
  public getAutoTrackerFactory(): AutoTrackerFactory {
    return this.autoTrackerFactory;
  }

  /**
   * Obtém estatísticas dos autoTracks
   */
  public getAutoTrackerStats(): ReturnType<
    typeof this.autoTrackerFactory.getFactoryStats
  > {
    return this.autoTrackerFactory.getFactoryStats();
  }

  /**
   * Para todos os autoTracks
   */
  public stopAllAutoTrackers(): void {
    this.autoTrackerFactory.stopAllAutoTrackers();
  }

  /**
   * Reconstrói todos os autoTracks
   */
  public async rebuildAllAutoTrackers(): Promise<void> {
    await this.autoTrackerFactory.rebuildAllAutoTrackers();
  }
}

// Exportação para uso em módulos
export default GomarkePixel;
